import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from "styled-components";
import { useSelector } from "react-redux";
import { selectCalendarDate } from "@shared/store/dashboard.duck";
import { selectFollowingTeams, selectFollowingWorkGroups } from '@features/whereabouts/store/followingTeamsAndTags.duck';
import { selectGlobalHeaderCollapsed, selectIsMonthView } from '@features/whereabouts/store/whereaboutsView.duck';
import { selectCurrentUser } from '@shared/store/auth.duck';
import { BodyRegular, BodySmall, BodyXXLSmall } from '@shared/ui/atoms/fonts/Body';
import { HeadlineSmall } from '@shared/ui/atoms/fonts/Headline';
import Colours from '@shared/ui/atoms/Colours';
import { SimpleSpinner } from '@shared/ui/atoms/SimpleSpinner';
import { useTranslation } from "react-i18next";
import { WithWeeklyNotesForDates } from '@shared/dialogs/weekly-notes/components/WithWeeklyNotesForDates';
import { WithWeeksInCurrentCalendarMonth } from '@features/whereabouts/hooks/WithWeekInCurrentCalendarMonth';
import { UserAvatarAndName } from '@shared/ui/molecules/UserAvatarAndName';
import { getWeeklyTeamNoteForUser, saveWeeklyNotes, WeekNote, WeekNoteScope } from '@features/whereabouts/services/WeekNotesService';
import Icon, { IconTypes } from '@shared/ui/atoms/icon/Icon';
import { isInTeams } from "@shared/utils/TeamsUtils";
import { whereaboutsHeaderOffset, whereaboutsViewportHeight } from "./Structure";
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import moment from "moment";
import { DATE_FORMAT } from "@shared/utils/DateUtils";
import { useWhereaboutsScope } from './WhereaboutsScopeContext';
import { useScopedGroups } from '@features/whereabouts/hooks/useScopedGroups';

interface Props {
  sidebarOpen?: boolean;
  onClose: () => void;
}

export function WeekNotesPanel({ sidebarOpen, onClose }: Props) {
  const { t } = useTranslation();
  const calendarDate = useSelector(selectCalendarDate);
  const followedTeams = useSelector(selectFollowingTeams);
  const followedWorkGroups = useSelector(selectFollowingWorkGroups);
  const currentUser = useSelector(selectCurrentUser);
  const headerCollapsed = useSelector(selectGlobalHeaderCollapsed);
  const isMonthView = useSelector(selectIsMonthView);
  const weeksInMonth = WithWeeksInCurrentCalendarMonth();
  const { scope, isScoped } = useWhereaboutsScope();
  const { teams: scopedTeams, workGroups: scopedWorkGroups } = useScopedGroups(scope);

  // On a page pinned to one team, only that team is listed. Otherwise the
  // user's own team is always shown alongside whatever they follow.
  const displayTeams = useMemo(() => {
    if (isScoped) return scopedTeams;
    const userTeam = currentUser?.teamEntity;
    if (!userTeam?.id) return followedTeams;
    if (followedTeams.some(t => t.id === userTeam.id)) return followedTeams;
    return [userTeam, ...followedTeams];
  }, [isScoped, scopedTeams, followedTeams, currentUser?.teamEntity]);

  // Query notes for the groups on display. On a scoped page that is the single pinned
  // team/workgroup; otherwise everything the user is following plus their own department.
  // The scope is resolved server-side from each note owner's membership.
  const scopes = useMemo<WeekNoteScope[]>(() => {
    if (isScoped) {
      return [
        ...scopedTeams.map(t => ({ type: 'TEAM', id: t.id } as WeekNoteScope)),
        ...scopedWorkGroups.map(w => ({ type: 'WORKGROUP', id: String(w.id) } as WeekNoteScope)),
      ];
    }
    const teamScopes: WeekNoteScope[] = displayTeams.map(t => ({ type: 'TEAM', id: t.id }));
    const workGroupScopes: WeekNoteScope[] = followedWorkGroups.map(w => ({ type: 'WORKGROUP', id: String(w.id) }));
    const departmentScopes: WeekNoteScope[] = currentUser?.department?.id
      ? [{ type: 'DEPARTMENT', id: String(currentUser.department.id) }]
      : [];
    return [...teamScopes, ...workGroupScopes, ...departmentScopes];
  }, [isScoped, scopedTeams, scopedWorkGroups, displayTeams, followedWorkGroups, currentUser?.department?.id]);
  // In month view, surface notes for every week shown in the month; otherwise just the focused week.
  const notesDates = useMemo(
    () => (isMonthView ? weeksInMonth : [calendarDate]),
    [isMonthView, weeksInMonth, calendarDate]
  );
  const { notes: weekNotes, loading: notesLoading, load: reloadWeekNotes } = WithWeeklyNotesForDates(scopes, notesDates);

  const [currentUserNotes, setCurrentUserNotes] = useState<WeekNote[]>([]);
  const [currentUserNoteLoading, setCurrentUserNoteLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const textRef = useRef<HTMLTextAreaElement>(null);

  // Load the current user's own notes for every week on display (all weeks of the month in month
  // view, otherwise just the focused week), tagging each with its week.
  const fetchCurrentUserNotes = useCallback(() => {
    if (!currentUser?.id || notesDates.length === 0) return;
    setCurrentUserNoteLoading(true);
    Promise.all(notesDates.map(async date => {
      const weekCommencing = date.clone().startOf('isoWeek').format(DATE_FORMAT);
      try {
        const note = await getWeeklyTeamNoteForUser(currentUser.id, date);
        return note?.note ? { ...note, weekCommencing } : null;
      } catch {
        return null;
      }
    }))
      .then(results => setCurrentUserNotes(results.filter((n): n is NonNullable<typeof n> => n !== null)))
      .finally(() => setCurrentUserNoteLoading(false));
  }, [currentUser?.id, notesDates]);

  useEffect(() => {
    fetchCurrentUserNotes();
  }, [fetchCurrentUserNotes]);

  // The focused week's note prefills the editor; saving always targets this week.
  const currentWeekUserNote = useMemo(() => {
    const key = calendarDate.clone().startOf('isoWeek').format(DATE_FORMAT);
    return currentUserNotes.find(n => n.weekCommencing === key) ?? null;
  }, [currentUserNotes, calendarDate]);

  const handleSave = async () => {
    if (saving) return;
    const noteText = textRef.current?.value ?? '';
    if (!currentUser?.id || !calendarDate) {
      setSaveError(t('week-notes.save-failed'));
      return;
    }
    setSaveError(null);
    setSaving(true);
    try {
      await saveWeeklyNotes(currentUser.id, calendarDate, noteText);
      fetchCurrentUserNotes();
      reloadWeekNotes();
      setEditing(false);
    } catch (err) {
      setSaveError(t('week-notes.save-failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !saving) {
      e.preventDefault();
      handleSave();
    }
  };

  const NO_TEAM_KEY = '__no_team__';

  // A user followed via several scopes (e.g. their team and a workgroup) can be returned more than
  // once; key by user + week so each note shows once per week, while a person's notes across
  // different weeks (month view) stay distinct.
  const dedupedNotes = useMemo(() => {
    const byKey = new Map<string, WeekNote>();
    weekNotes.forEach(note => byKey.set(`${note.userId}-${note.weekCommencing ?? ''}-${note.notesId ?? 'none'}`, note));
    return Array.from(byKey.values());
  }, [weekNotes]);

  const teamNotesByTeam = useMemo(() => {
    const grouped = new Map<string, { teamName: string; notes: WeekNote[] }>();
    dedupedNotes.forEach(note => {
      const key = note.teamId ?? NO_TEAM_KEY;
      const existing = grouped.get(key);
      if (existing) {
        existing.notes.push(note);
      } else {
        grouped.set(key, { teamName: note.teamName ?? t('week-notes.no-team', 'No team'), notes: [note] });
      }
    });
    return grouped;
  }, [dedupedNotes, t]);

  // Always render the user's followed teams (with an empty state), then any other teams surfaced by
  // workgroup/department scopes.
  const sections = useMemo(() => {
    const result: { id: string; name: string }[] = [];
    const seen = new Set<string>();
    displayTeams.forEach(team => {
      if (!seen.has(team.id)) {
        seen.add(team.id);
        result.push({ id: team.id, name: team.name });
      }
    });
    teamNotesByTeam.forEach((value, key) => {
      if (!seen.has(key)) {
        seen.add(key);
        result.push({ id: key, name: value.teamName });
      }
    });
    return result;
  }, [displayTeams, teamNotesByTeam]);

  const weekLabel = (weekCommencing?: string) =>
    weekCommencing
      ? t('week-notes.week-of', { date: moment(weekCommencing).format('Do MMM'), defaultValue: 'Week of {{date}}' })
      : '';

  const renderNote = (note: WeekNote) => (
    <NoteCard key={`${note.userId}-${note.weekCommencing ?? ''}-${note.notesId ?? 'none'}`} isCurrentUser={note.userId === currentUser?.id}>
      <NoteCardHeader>
        <UserAvatarAndName
          firstName={note.firstName}
          lastName={note.lastName}
          hideName={false}
          colour={note.userId === currentUser?.id ? 'darkBlue' : 'blue'}
        />
      </NoteCardHeader>
      {isMonthView && note.weekCommencing && (
        <WeekLabel><BodyXXLSmall colour={Colours.darkGrey}>{weekLabel(note.weekCommencing)}</BodyXXLSmall></WeekLabel>
      )}
      <NoteContent>
        <BodySmall style={{ whiteSpace: 'pre-wrap' }}>{note.note}</BodySmall>
      </NoteContent>
      <NoteTimestamp>
        <BodyXXLSmall colour={Colours.darkGrey}>
          {t('dialog.week-note-last-updated', { lastUpdated: moment(note.lastUpdated).format(DATE_FORMAT) })}
        </BodyXXLSmall>
      </NoteTimestamp>
    </NoteCard>
  );

  return (
    <Panel $sidebarOpen={sidebarOpen} $headerCollapsed={headerCollapsed}>
      <PanelHeader>
        <HeadlineSmall>{t('week-notes.week-notes')}</HeadlineSmall>
        <PanelClose onClick={onClose}>
          <Icon icon={IconTypes.Close} size="small" color="black" />
        </PanelClose>
      </PanelHeader>
      {editing ? (
        <EditSection>
          <EditTextArea
            ref={textRef}
            defaultValue={currentWeekUserNote?.note ?? ''}
            disabled={saving}
            onKeyDown={handleKeyDown}
            placeholder={t('week-notes.enter-your-note')}
            autoFocus
          />
          {saveError && <SaveError>{saveError}</SaveError>}
          <EditButtons>
            <OutlineButton text={'button.cancel'} click={() => { setSaveError(null); setEditing(false); }} disabled={saving} style={{marginRight: 8}} />
            <OutlineButton text={'button.done'} click={() => handleSave()} disabled={saving} />
          </EditButtons>
        </EditSection>
      ) : (
        <SetNoteButton onClick={() => setEditing(true)}>
          <Icon icon={IconTypes.Edit} size="small" />
          <BodySmall weight={600}>{t('week-notes.set-my-note')}</BodySmall>
        </SetNoteButton>
      )}
      {notesLoading && currentUserNoteLoading ? (
        <LoadingContainer><SimpleSpinner size={24} /></LoadingContainer>
      ) : (
        <NotesList>
          <CurrentUserRow>
            {currentUserNoteLoading ? (
              <SimpleSpinner size={20} />
            ) : currentUserNotes.length === 0 ? (
              <BodySmall colour={Colours.darkGrey}>{t('week-notes.no-note-set')}</BodySmall>
            ) : (
              <CurrentUserNotes>
                {currentUserNotes.map(note => (
                  <CurrentUserNoteItem key={note.weekCommencing ?? note.notesId}>
                    {isMonthView && note.weekCommencing && (
                      <BodyXXLSmall colour={Colours.darkGrey}>{weekLabel(note.weekCommencing)}</BodyXXLSmall>
                    )}
                    <BodySmall style={{ whiteSpace: 'pre-wrap' }}>{note.note}</BodySmall>
                  </CurrentUserNoteItem>
                ))}
              </CurrentUserNotes>
            )}
          </CurrentUserRow>
          {sections.map(section => {
            const teamNotes = teamNotesByTeam.get(section.id);
            return (
              <NotesSection key={section.id}>
                <SectionTitle>{section.name}</SectionTitle>
                {teamNotes ? teamNotes.notes.map(renderNote) : (
                  <NoNotesMessage><BodySmall colour={Colours.darkGrey}>{t('week-notes.no-notes-for-team')}</BodySmall></NoNotesMessage>
                )}
              </NotesSection>
            );
          })}
        </NotesList>
      )}
    </Panel>
  );
}

const Panel = styled.div<{ $sidebarOpen?: boolean; $headerCollapsed?: boolean }>`
  position: fixed;
  right: ${props => props.$sidebarOpen ? '360px' : '80px'};
  top: ${props => whereaboutsHeaderOffset(isInTeams(), props.$headerCollapsed)};
  width: 320px;
  height: ${props => whereaboutsViewportHeight(isInTeams(), props.$headerCollapsed)};
  background: white;
  border-left: 1px solid ${Colours.lightGrey};
  overflow-y: auto;
  z-index: 1050;
  transition: right 0.25s ease, top 0.25s ease, height 0.25s ease;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid ${Colours.lightGrey};
`;

const PanelClose = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
  color: ${Colours.darkGrey};
  padding: 4px 8px;
  border-radius: 4px;
  &:hover {
    background: ${Colours.veryLightGrey};
  }
`;

const SetNoteButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 12px 16px;
  background: none;
  border: none;
  border-bottom: 1px solid ${Colours.lightGrey};
  cursor: pointer;
  .icon {
    font-size: 1em;
  }
  &:hover {
    background: ${Colours.veryLightGrey};
  }
`;

const EditSection = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid ${Colours.lightGrey};
`;

const EditTextArea = styled.textarea`
  width: 100%;
  min-height: 100px;
  margin-bottom: 8px;
  padding: 8px;
  font-size: 14px;
  font-family: "soleil", sans-serif;
  border: 1px solid ${Colours.lightGrey};
  border-radius: 6px;
  resize: vertical;
  &:focus {
    outline: none;
    border-color: ${Colours.blue};
  }
`;

const EditButtons = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const SaveError = styled.div`
  color: ${Colours.red};
  font-size: 12px;
  margin-bottom: 8px;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 32px;
`;

const NoNotesMessage = styled.div`
  padding: 8px 4px;
`;

const NotesList = styled.div`
  padding: 8px;
`;

const CurrentUserRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 4px;
`;

const CurrentUserNotes = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const CurrentUserNoteItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const WeekLabel = styled.div`
  margin-bottom: 4px;
`;

const NotesSection = styled.div`
  margin-bottom: 12px;
`;

const SectionTitle = styled(BodyRegular)`
  font-weight: 700;
  padding: 8px 4px 4px;
  color: ${Colours.darkGrey};
`;

const NoteCard = styled.div<{ isCurrentUser: boolean }>`
  background: ${props => props.isCurrentUser ? '#EEF0FF' : Colours.veryLightGrey};
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 8px;
`;

const NoteCardHeader = styled.div`
  margin-bottom: 8px;
  .userAvatarAndName {
    align-items: center;
  }
`;

const NoteContent = styled.div`
  margin-bottom: 8px;
`;

const NoteTimestamp = styled.div`
  text-align: right;
`;
